import type { LiveSlotHost } from "@hobbyshopify/backend-client";
import { formatCentsCompact } from "../../core/primitives/live";

/**
 * The spot grid — the one thing both sides of a break look at.
 *
 * The host reads it to know what is left and, after the draw, who got what. A
 * buyer reads the same tiles to find their own. One component for both, because two would drift and the moment they
 * disagree is the moment a host is describing a different grid from the one the
 * room is watching.
 */

export function FillProgress({ soldCount, slotCount }: { soldCount: number; slotCount: number }) {
  const pct = slotCount === 0 ? 0 : Math.round((soldCount / slotCount) * 100);

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="opacity-60">Filled</span>
        <span className="font-semibold tabular-nums">
          {soldCount}/{slotCount}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-surface)]">
        <div
          className="h-full rounded-full bg-[color:var(--color-accent)] transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Who has a spot, in the words the room uses; null while it is free. */
function holder(slot: LiveSlotHost): string | null {
  if (slot.status === "HOUSE") return "House";
  if (slot.status === "SOLD") return slot.buyerName ?? "Sold";
  if (slot.status === "HELD") return "Held";
  return null;
}

export function SpotGrid({
  slots,
  readOnly = false,
  priceCents,
}: {
  slots: readonly LiveSlotHost[];
  /** The viewer's copy: no host notes. */
  readOnly?: boolean;
  /** The box's price, said once above the grid; a tile names its own only when it differs. */
  priceCents?: number;
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {slots.map((slot) => {
        const who = holder(slot);
        const ownPrice = priceCents === undefined || slot.priceCents !== priceCents;

        return (
          <div
            key={slot.id}
            className={`flex min-h-11 flex-col items-start rounded-lg border px-1.5 py-1 text-left ${
              who ? "border-[color:var(--color-accent)]/40 bg-white/5" : "border-dashed border-white/10"
            }`}
          >
            <span className="flex w-full items-baseline justify-between gap-1 text-[11px] opacity-50">
              <span className="tabular-nums">#{slot.slotNumber}</span>
              {ownPrice && <span>{formatCentsCompact(slot.priceCents)}</span>}
            </span>
            {slot.assignedLabel && <span className="w-full truncate text-xs font-semibold">{slot.assignedLabel}</span>}
            <span className={`w-full truncate text-xs ${who ? "" : "opacity-30"}`}>{who ?? "—"}</span>
            {/* Host-only, and only where it changes what happens next. */}
            {!readOnly && slot.sellerNote && (
              <span className="w-full truncate text-[11px] opacity-50">{slot.sellerNote}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
